import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Save, X, Wand2, Loader2, 
  FileText, Sparkles, AlertCircle, Check, Tag
} from 'lucide-react';
import { websearchService } from '../../services/websearchService';
import { functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';

export const SearchPromptManager = ({ 
  isOpen, 
  onClose, 
  bookId, 
  isDarkMode = true 
}) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    categoryName: '',
    prompt: ''
  });
  const [refining, setRefining] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadPrompts();
    }
  }, [isOpen, bookId]);

  const loadPrompts = async () => {
    setLoading(true);
    try {
      const data = await websearchService.getSearchPrompts(bookId);
      setPrompts(data);
    } catch (error) {
      console.error('Error loading prompts:', error);
      setError('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      categoryName: prompt.categoryName,
      prompt: prompt.prompt
    });
    setShowForm(true);
  };

  const handleDelete = async (promptId) => {
    if (!window.confirm('Are you sure you want to delete this search prompt?')) {
      return;
    }

    try {
      await websearchService.deleteSearchPrompt(bookId, promptId);
      setPrompts(prev => prev.filter(p => p.id !== promptId));
      setSuccess('Prompt deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error deleting prompt:', error);
      setError('Failed to delete prompt');
    }
  };

  const handleSave = async () => {
    if (!formData.categoryName.trim() || !formData.prompt.trim()) {
      setError('Category name and prompt are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editingPrompt) {
        await websearchService.updateSearchPrompt(bookId, editingPrompt.id, formData);
        setPrompts(prev => prev.map(p => 
          p.id === editingPrompt.id ? { ...p, ...formData } : p
        ));
        setSuccess('Prompt updated successfully');
      } else {
        const newPrompt = await websearchService.createSearchPrompt(bookId, formData);
        setPrompts(prev => [newPrompt, ...prev]);
        setSuccess('Prompt created successfully');
      }

      setShowForm(false);
      setEditingPrompt(null);
      setFormData({ categoryName: '', prompt: '' });
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving prompt:', error);
      setError('Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleRefineWithAI = async () => {
    if (!formData.prompt.trim()) {
      setError('Enter a prompt first');
      return;
    }

    setRefining(true);
    setError(null);

    try {
      const refinePrompt = httpsCallable(functions, 'refineSearchPrompt');
      const result = await refinePrompt({
        prompt: formData.prompt,
        categoryName: formData.categoryName
      });

      setFormData(prev => ({
        ...prev,
        prompt: result.data.refinedPrompt
      }));
    } catch (err) {
      console.error('Error refining prompt:', err);
      setError('Failed to refine prompt');
    } finally {
      setRefining(false);
    }
  };

  if (!isOpen) return null;

  const bgClass = isDarkMode ? 'bg-slate-900' : 'bg-white';
  const textClass = isDarkMode ? 'text-white' : 'text-gray-900';
  const borderClass = isDarkMode ? 'border-slate-700' : 'border-gray-200';
  const cardBgClass = isDarkMode ? 'bg-slate-800' : 'bg-gray-50';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${bgClass} w-full max-w-5xl rounded-xl shadow-2xl flex flex-col max-h-[90vh]`}>
        {/* Header */}
        <div className={`p-6 border-b ${borderClass}`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className={`text-2xl font-bold ${textClass} flex items-center gap-2`}>
                <Sparkles className="h-6 w-6 text-indigo-500" />
                Search Prompts Manager
              </h2>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Create custom search categories with AI-powered prompts
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? 'hover:bg-slate-800 text-slate-400 hover:text-white'
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-500/10 border border-green-500/50 rounded-lg flex items-center gap-2 text-green-400 text-sm">
              <Check className="h-4 w-4" />
              {success}
            </div>
          )}

          {/* Add New Button */}
          {!showForm && (
            <button
              onClick={() => {
                setShowForm(true);
                setEditingPrompt(null);
                setFormData({ categoryName: '', prompt: '' });
              }}
              className="w-full mb-6 p-4 border-2 border-dashed rounded-lg transition-colors flex items-center justify-center gap-2 font-medium ${
                isDarkMode
                  ? 'border-slate-700 text-slate-400 hover:border-indigo-500 hover:text-indigo-400 hover:bg-slate-800/50'
                  : 'border-gray-300 text-gray-600 hover:border-indigo-500 hover:text-indigo-600 hover:bg-indigo-50'
              }"
            >
              <Plus className="h-5 w-5" />
              Create New Search Prompt
            </button>
          )}

          {/* Form */}
          {showForm && (
            <div className={`mb-6 p-6 rounded-lg border ${borderClass} ${cardBgClass}`}>
              <h3 className={`text-lg font-semibold mb-4 ${textClass}`}>
                {editingPrompt ? 'Edit Search Prompt' : 'New Search Prompt'}
              </h3>

              <div className="space-y-4">
                {/* Category Name */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-slate-300' : 'text-gray-700'
                  }`}>
                    Category Name *
                  </label>
                  <div className="relative">
                    <Tag className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${
                      isDarkMode ? 'text-slate-500' : 'text-gray-400'
                    }`} />
                    <input
                      type="text"
                      value={formData.categoryName}
                      onChange={(e) => setFormData(prev => ({ ...prev, categoryName: e.target.value }))}
                      placeholder="e.g., Youtube Science, Property for Sale, Job Openings"
                      className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        isDarkMode
                          ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      }`}
                    />
                  </div>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                    This will be the name of your search category
                  </p>
                </div>

                {/* Prompt */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-slate-300' : 'text-gray-700'
                  }`}>
                    Search Prompt *
                  </label>
                  <textarea
                    value={formData.prompt}
                    onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
                    rows={8}
                    placeholder='Example: Find 10 popular science YouTube channels about space exploration. Return JSON array with: channelName, channelUrl, subscribers, description, recentVideos'
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm resize-none ${
                      isDarkMode
                        ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                    Use {'{query}'} as a placeholder for the user's search input. Request JSON array format.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={handleRefineWithAI}
                    disabled={refining || !formData.prompt.trim()}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition-all"
                  >
                    {refining ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Refining...
                      </>
                    ) : (
                      <>
                        <Wand2 className="h-4 w-4" />
                        Refine with AI
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => {
                      setShowForm(false);
                      setEditingPrompt(null);
                      setFormData({ categoryName: '', prompt: '' });
                    }}
                    className={`px-4 py-2 rounded-lg border font-medium transition-colors ${
                      isDarkMode
                        ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Prompts List */}
          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className={`h-8 w-8 animate-spin mx-auto ${
                  isDarkMode ? 'text-slate-500' : 'text-gray-400'
                }`} />
              </div>
            ) : prompts.length === 0 ? (
              <div className="text-center py-12">
                <FileText className={`h-12 w-12 mx-auto mb-3 ${
                  isDarkMode ? 'text-slate-600' : 'text-gray-300'
                }`} />
                <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>
                  No search prompts yet. Create your first one!
                </p>
              </div>
            ) : (
              prompts.map(prompt => (
                <div
                  key={prompt.id}
                  className={`p-4 rounded-lg border ${borderClass} ${cardBgClass} hover:border-indigo-500 transition-colors`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Tag className="h-4 w-4 text-indigo-500" />
                        <h4 className={`font-semibold ${textClass}`}>
                          {prompt.categoryName}
                        </h4>
                      </div>
                      <p className={`text-sm font-mono ${
                        isDarkMode ? 'text-slate-400' : 'text-gray-600'
                      } line-clamp-2`}>
                        {prompt.prompt}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleEdit(prompt)}
                        className={`p-2 rounded-lg transition-colors ${
                          isDarkMode
                            ? 'hover:bg-slate-700 text-slate-400 hover:text-white'
                            : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(prompt.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          isDarkMode
                            ? 'hover:bg-red-900/20 text-slate-400 hover:text-red-400'
                            : 'hover:bg-red-50 text-gray-500 hover:text-red-600'
                        }`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};